<script setup lang="ts">
import { ButtonComp, ContentComp, InputBool, InputInt, ModalComp } from '@/components';
import type { TaskLoaderState } from '@/composeables';
import type { Eventinfo } from '@/objects/generated';
import { ref, type ComponentInstance } from 'vue';

const event = defineModel<Eventinfo>('event', { required: true });
const keepAssigned = defineModel<boolean>('keepAssigned', { required: true });
const overwriteStartTimes = defineModel<boolean>('overwriteStartTimes', { required: true });
const shuffle = defineModel<boolean>('shuffle', { required: true });

const props = defineProps<{
    assignLoadState: TaskLoaderState | null;
}>();

const emit = defineEmits<{
    (e: 'confirm'): void;
}>();

const assignDialog = ref<ComponentInstance<typeof ModalComp> | null>(null);

defineExpose({
    show: () => assignDialog.value?.show(),
    hide: () => assignDialog.value?.hide(),
});
</script>

<template>
    <ModalComp
        ref="assignDialog"
        title="bibassign.assignconfirm"
    >
        <template #content>
            <ContentComp class="p-3">
                <div class="mb-3">
                    <span v-html="$tm('bibassign.assignconfirmmessage')"></span>
                </div>
                <div class="mb-1">
                    <span v-html="$tm('bibassign.reservebibs_info')"></span>
                </div>
                <div class="d-flex gap-2 mb-1">
                    <InputInt v-model="event.ReserveBibsFrom" /> -
                    <InputInt v-model="event.ReserveBibsTo" />
                </div>
                <div class="d-flex">
                    <InputBool
                        v-model="keepAssigned"
                        :label="$tm('bibassign.keepassigned', 'Keep assigned', true)"
                    />
                </div>
                <div class="d-flex">
                    <InputBool
                        v-model="overwriteStartTimes"
                        :label="$tm('bibassign.overwritestarttimes')"
                        :tooltip="$tm('bibassign.overwritestarttimesdescription')"
                    />
                </div>
                <div class="d-flex mt-2">
                    <InputBool
                        v-model="shuffle"
                        :label="$tm('bibassign.shuffle')"
                        :tooltip="$tm('bibassign.shuffledescription')"
                    />
                </div>
            </ContentComp>
        </template>
        <template #footer>
            <div class="d-flex justify-content-end gap-2">
                <ButtonComp
                    button-type="Secondary"
                    icon="fa-times"
                    @click="() => assignDialog?.hide()"
                >
                    {{ $tm('button.cancel') }}
                </ButtonComp>
                <ButtonComp
                    :loadstate="props.assignLoadState"
                    button-type="Primary"
                    icon="fa-check"
                    @click="emit('confirm')"
                >
                    {{ $tm('bibassign.assign') }}
                </ButtonComp>
            </div>
        </template>
    </ModalComp>
</template>
